import { utils } from '@digital-commerce/shared-utils'
import {
  filter,
  includes,
  map,
  path,
  pickBy,
  pipe,
  propEq,
  pathOr,
  find,
  has,
  prop,
  curry,
  values,
  pathEq,
  cond,
  allPass,
  complement,
  isEmpty,
  applySpec,
  head,
  always,
  T,
  any,
  not,
  both,
  propOr,
  gt,
  lt,
  __,
  anyPass,
  equals,
  gte,
  either,
  mergeAll,
  reduce,
  assoc,
  keys,
  hasPath,
  nth,
  none
} from 'ramda'
import { formatCurrency } from './currency'

const { getTopLevelItems, _getItems } = utils.cart

export const getMobilityOffers = pipe(
  getTopLevelItems,
  filter(propEq('category', 'MOBILITY_OFFER'))
)

export const isMobilePhone = allPass([
  propEq(['category'], 'MOBILITY_PHONE'),
  complement(
    anyPass([
      pipe(prop('tags'), includes('smartwatch')),
      pipe(prop('tags'), includes('connected_products')),
      pipe(prop('tags'), includes('homephone'))
    ])
  )
])

export const isTablet = pipe(
  allPass([
    pathEq(['category'], 'MOBILITY_TABLET'),
    pipe(prop('tags'), both(includes('connected_products'), includes('tablet')))
  ])
)

export const isSmartwatch = pipe(
  allPass([
    pathEq(['category'], 'MOBILITY_PHONE'),
    pipe(prop('tags'), allPass([includes('smartwatch'), includes('connected_products')]))
  ])
)

/**
 * Gets a list of top-level mobility offers with populated sub-items data
 * @param {object} cart Cart response
 * @returns {object[]} A list of mobility offers with sub-items
 */
export const getMobilityOffersWithItems = (cart) =>
  pipe(
    getMobilityOffers,
    map((offer) => ({
      ...offer,
      items: pickBy((val, key) => includes(key, offer.items), cart.items)
    }))
  )(cart)

export const getChangeUrlFromMobilityOffer = (offer) => {
  const editUrl = pathOr('', ['system', 'editUrl'])(offer)
  const queryStringCharacter = editUrl.includes('?') ? '&' : '?'
  return editUrl ? `${editUrl}${queryStringCharacter}itemId=${offer.id}` : ''
}

/**
 * Get a sub-item related to the mobility offer by category
 * @param {string} category Category of the item
 * @param {object} offer Top-level mobility offer
 * @return {object} Sub-item
 */
const getSubItemFromOfferByCategory = curry((category, offer) => {
  return pipe(prop('items'), values, find(propEq('category', category)))(offer)
})

export const getSimItem = (offer) => getSubItemFromOfferByCategory('MOBILITY_SIM')(offer)

export const getMobilityPlan = (offer) => getSubItemFromOfferByCategory('MOBILITY_PLAN')(offer)

export const getMobilityGifts = (offer) =>
  pipe(
    prop('items'),
    values,
    filter(
      anyPass([
        propEq('category', 'MOBILITY_GWP_MANUFACTURER'),
        propEq('category', 'MOBILITY_GWP_TELUS')
      ])
    )
  )(offer)

export const getConnectionFee = (offer) =>
  getSubItemFromOfferByCategory('MOBILITY_CONNECTION_FEE')(offer)

export const getMobilityWarranty = (offer) =>
  getSubItemFromOfferByCategory('MOBILITY_WARRANTY')(offer)

export const getEasyRoamOffers = (offer) =>
  pipe(
    _getItems,
    filter(pathEq(['productOffering', 'characteristics', 0, 'value'], 'EASYROAM'))
  )(offer)

export const getVASAddOns = (offer) =>
  pipe(_getItems, filter(pathEq(['productOffering', 'characteristics', 0, 'value'], 'VAS')))(offer)

const getVASName = path(['productOffering', 'displayName'])

const moveTosToLast = (addOns) =>
  addOns?.sort((a) => (a?.productOffering.displayName.toLowerCase().includes('security') ? 1 : -1))

export const getConnectionFeeValues = (connectionFeeItem) => ({
  reference: parseFloat(pathOr('0', ['content', 'value'])(connectionFeeItem)),
  actual: pipe(
    pathOr([], ['itemPrice', 'recurring']),
    find(has('amount')),
    prop('amount')
  )(connectionFeeItem)
})

export const getTerm = pipe(
  path(['itemPrice', 'recurring']),
  find(has('recurringDuration')),
  prop('recurringDuration')
)

export const getConnectionFeeAttribute = (connectionFee, translate) => {
  const { actual } = getConnectionFeeValues(connectionFee)
  const connectionFeeAttribute =
    actual === 0 ? 'item.connectionFeeWaivedAttribute' : 'item.connectionFeeAppliedAttribute'
  return translate(connectionFeeAttribute)
}

const addOnLabel = (addOns, translate) =>
  addOns.length <= 1 ? translate('addon') : translate('addons')

const getVASText = (vas, translate) => {
  return vas.reduce((acc, cur, index) => {
    if (index === 0) return `${acc}: ${getVASName(cur)}`
    return `${acc}, ${getVASName(cur)}`
  }, addOnLabel(vas, translate))
}

/**
 * Get the formatted vas text to be mapped with other attributes
 * @param {object[]} mobilityVASitems A list of retrieved mob VAS items
 * @param {function} translate
 * @returns {string[]}
 */
export const getMobVASAttributeList = (mobilityVASitems, translate) => {
  const vasAttributes = mobilityVASitems.length
    ? [getVASText(moveTosToLast(mobilityVASitems), translate)]
    : []
  return vasAttributes
}

const getMobCustomerBan = pathOr('', ['root', 'customer', 'mobility', 'ban'])

const hasMobilityOfferType = curry((orderType, cart) => {
  return pipe(
    _getItems,
    pipe(
      filter(
        allPass([propEq('category', 'MOBILITY_OFFER'), pathEq(['productOrder', 'type'], orderType)])
      ),
      complement(isEmpty)
    )
  )(cart)
})

const hasMobilityAALInCart = hasMobilityOfferType('AAL')

const hasMobilityRenewalInCart = hasMobilityOfferType('RENEWAL')

export const hasMobilityActivationInCart = hasMobilityOfferType('ACTIVATION')

export const addDashesToPhoneNumber = (phoneNumber) => {
  const phoneNumberValue = phoneNumber
    .replace(/\D/g, '')
    .match(/(?<prefix>1)?(?<first>\d{0,3})(?<second>\d{0,3})(?<third>\d{0,4})/)

  const phoneNumberGroups = Object.values(phoneNumberValue.groups).filter(Boolean)

  return phoneNumberGroups.join('-')
}

/**
 * Gets data for AAL or Renewal to display within cart item notification banner
 * @param {object} cart - Cart response
 * @return {object}
 */
export const getAALOrRenewalBannerMessage = cond([
  [
    hasMobilityAALInCart,
    applySpec({
      type: always('AAL'),
      mobInfo: getMobCustomerBan,
      translationKey: always('customerBanMessage')
    })
  ],
  [
    hasMobilityRenewalInCart,
    applySpec({
      type: always('RENEWAL'),
      mobInfo: pipe(
        getMobilityOffers,
        head,
        pathOr('', ['account', 'sub']),
        addDashesToPhoneNumber
      ),
      translationKey: always('renewalBannerMessage')
    })
  ],
  [T, always(undefined)]
])

const notVASAddOn = pipe(
  pathOr([], ['productOffering', 'characteristics']),
  any(propEq('name', 'featureCategory')),
  not
)

/**
 * Gets a list of non-value added services add-ons
 * @param {object} offer - Top-level offer item
 * @returns {object[]} A list of mobility plan add-ons
 */
export const getMobilityAddOns = pipe(
  prop('items'),
  values,
  filter(both(propEq(['category'], 'MOBILITY_PLAN_ADD_ON'), notVASAddOn))
)

export const getLastMobilityAddon = pipe(getMobilityAddOns, nth(-1))

export const isEsim = (mobilitySIMItem) => pathOr(false, ['system', 'isESIME'])(mobilitySIMItem)

export const getSimTypeAttribute = (simItem, offer, lang, translate) => {
  const simType = isEsim(simItem)
    ? translate('item.mobilitySIM.eSim')
    : translate('item.mobilitySIM.pSim')

  const addOn = getMobilityAddOns(offer).find((item) => item.productOffering?.id.includes('SIMACC'))
  if (addOn) {
    const simCost = addOn.itemPrice?.recurring[0]?.amount
    const simPrefix = translate('item.mobilitySIM.prefix')
    return `${simType}${simPrefix}${formatCurrency(simCost, lang)} ${translate(
      'item.mobilityAddOn'
    )}`
  }
  return simType
}

export const getItemRecurringCredits = (item) =>
  pipe(
    pathOr([], ['itemPrice', 'recurring']),
    find(has('alterations')),
    propOr([], 'alterations'),
    filter(allPass([pathEq(['category'], 'CREDIT'), pipe(prop('amount'), gt(__, 0))]))
  )(item)

/**
 * Gets a list of infinite discounts (recurringDuration = 0) and
 * if provided a term, it will also return the recurring discount that is >= to term.
 * When term is not supplied the default is 24.
 * @param {object} item - Item from cart
 * @param {boolean} term - Number of months for term (ie. 12, 24)
 * @returns {object[]} A list of recurring discounts
 */
export const getItemRecurringDiscounts = (item, term = 24) =>
  pipe(
    pathOr([], ['itemPrice', 'recurring']),
    find(has('alterations')),
    propOr([], 'alterations'),
    filter(
      allPass([
        pathEq(['category'], 'DISCOUNT'),
        anyPass([
          pipe(prop('recurringDuration'), equals(0)),
          pipe(prop('recurringDuration'), gte(__, term))
        ])
      ])
    )
  )(item)

/**
 * Gets a list of non-recurring discounts and if
 * provided a term, it will also return the non-recurring discount that is <= to term.
 * When term is not supplied the default is 24.
 * @param {object} item - Item from cart
 * @param {number} term - Number of months for term (ie. 12, 24)
 * @returns {object[]} A list of non-recurring discounts
 */
export const getItemNonRecurringDiscounts = (item, term = 24) =>
  pipe(
    pathOr([], ['itemPrice', 'recurring']),
    find(has('alterations')),
    propOr([], 'alterations'),
    filter(
      allPass([
        pathEq(['category'], 'DISCOUNT'),
        pipe(path(['recurringDuration']), gt(__, 1)),
        pipe(path(['recurringDuration']), lt(__, term))
      ])
    )
  )(item)

/**
 * Gets a list of non-recurring discounts from a list of items.
 * See getItemNonRecurringDiscounts function for further docs.
 * @param {object[]} items - List of items from cart
 * @param {number} term Number of months for term (ie. 12, 24)
 * @returns {object[]} A list of non-recurring discounts
 */
export const getItemsNonRecurringDiscounts = (items, term) => {
  return items.flatMap((item) => getItemNonRecurringDiscounts(item, term))
}

/**
 * Builds a list of one time discounts for a certain item/device.
 * Usually used for finding discounts off due today price for an item.
 * @param {object} item - Item with discounts (phone / connected device, etc.)
 * @returns {object[]} A list of one time discounts for the item
 */
export const getOneTimeItemDiscounts = (item) =>
  pipe(
    pathOr([], ['itemPrice', 'oneTime']),
    find(allPass([has('alterations')])),
    propOr([], 'alterations'),
    filter(pathEq(['category'], 'DISCOUNT'))
  )(item)

export const productDetailsFactory = (mobilityTopLevelItem) => {
  const contentPaths = {
    storage: path(['storage', 'fields', 'ref']),
    colour: path(['colour', 'fields', 'name'])
  }

  const topLevelItemContent = (item) => {
    const getStorage = () => {
      return pipe(propOr({}, 'content'), contentPaths.storage)(item)
    }

    const getColour = () => {
      return pipe(propOr({}, 'content'), contentPaths.colour)(mobilityTopLevelItem)
    }

    return {
      get storage() {
        return getStorage()
      },
      get colour() {
        return getColour()
      }
    }
  }

  const topLevelItemCharacteristics = (item) => {
    const getStorage = () => {
      return pipe(
        pathOr([], ['productOffering', 'characteristics']),
        find(propEq('name', 'storage')),
        prop('displayValue')
      )(item)
    }

    const getColour = () => {
      return pipe(
        pathOr([], ['productOffering', 'characteristics']),
        find(propEq('name', 'colour')),
        prop('displayValue')
      )(item)
    }

    return {
      get storage() {
        return getStorage()
      },
      get colour() {
        return getColour()
      }
    }
  }
  const hasContentProperty = hasPath(['content'], mobilityTopLevelItem)
  if (hasContentProperty) {
    return topLevelItemContent(mobilityTopLevelItem)
  }
  return topLevelItemCharacteristics(mobilityTopLevelItem)
}

export const getOneTimeFinancedAmountCredit = (item) =>
  pipe(
    pathOr({}, ['itemPrice', 'oneTime']),
    find(allPass([has('alterations')])),
    propOr([], 'alterations'),
    find(pathEq(['type'], 'financedAmount')),
    prop('amount')
  )(item)

export const getFinancedAccessoryPenalties = pipe(
  pathOr([], ['itemPrice', 'recurring']),
  filter(propEq('name', 'PENALTY_ACCESSORY_FINANCE'))
)

export const getFinancedAccessoryGroups = pipe(
  getTopLevelItems,
  filter(propEq('category', 'MOBILITY_ACCESSORY_GROUP'))
)
export const getMonthlyTotalPriceOfTerm = (term, offer) => {
  const fallbackTerm = 25
  return pipe(
    pathOr([], ['totalPrice', 'recurring']),
    find(
      allPass([
        pathEq(['recurringPeriod', 'every'], 1),
        pathEq(['recurringPeriod', 'unit'], 'MONTH')
      ])
    ),
    propOr([], 'recurringBreakdowns'),
    filter(pipe(propOr(1, 'start'), lt(__, term || fallbackTerm))),
    nth(-1)
  )(offer)
}

export const isPhone = pipe(prop('tags'), includes('phone'))

export const shouldRenderRemoveFinancedOfferBanner = (cartItem, hasFinancedAccessoryGroup) => {
  const mobOffer = cartItem?.category === 'MOBILITY_OFFER'
  const isOfferAAL = (offer) => {
    return pathEq(['productOrder', 'type'], 'AAL')(offer)
  }
  const isOfferActivation = (offer) => {
    return pathEq(['productOrder', 'type'], 'ACTIVATION')(offer)
  }
  const isRenewalOrderType = (offer) => {
    return pathEq(['productOrder', 'type'], 'RENEWAL')(offer)
  }

  return (
    mobOffer &&
    hasFinancedAccessoryGroup &&
    (isOfferAAL(cartItem) || isOfferActivation(cartItem) || isRenewalOrderType(cartItem))
  )
}

export const getParentItemWithNestedSubItems = (parentItem, cart) => {
  const item = {
    ...parentItem,
    items: pickBy((val, key) => includes(key, parentItem.items), cart.items)
  }

  return item
}

export const hasActiveContract = (offer) =>
  Boolean(
    offer?.itemPrice?.oneTime.some(
      (contract) => contract?.name === 'PENALTY_HARDWARE' || contract?.name === 'PENALTY_BIB'
    )
  )

// **** start of ORBIT utils **** //

/**
 * Gets bring-it-back (BIB) pending alterations from offer
 * @param {object} offer - Top-level offer item from cart
 * @returns {object[]} A list of BIB pending alterations
 */
export const getPenaltyBIBPending = pipe(
  pathOr([], ['itemPrice', 'oneTime']),
  find(propEq('name', 'PENALTY_BIB')),
  propOr([], 'alterations'),
  filter(pipe(prop('name'), includes('_PENDING', __)))
)

const _isBringItBack = propEq(['category'], 'MOBILITY_BRING_IT_BACK')
const isTradeIn = propEq(['category'], 'MOBILITY_TRADE_IN')

/**
 * Gets either trade-in or BIB orbit item from top-level offer
 * @param {object} offer - Top-level offer
 * @returns {object} - Orbit item
 */
export const getOrbitItemFromOffer = pipe(
  prop('items'),
  values,
  find(either(isTradeIn, _isBringItBack))
)

export const getOrbitItemRecurringCredits = pipe(
  pathOr([], ['itemPrice', 'recurring']),
  find(has('alterations')),
  propOr([], 'alterations'),
  filter(allPass([pathEq(['category'], 'CREDIT')]))
)

const isOrbitTradeInDeviceCredit = pipe(prop('name'), includes('tradeInDeviceCredit'))

const isOrbitOneTimeCreditType = pipe(prop('name'), includes('One-Time Bill Credit'))

const isOrbitBibDeviceCreditType = pipe(prop('name'), includes('bibDeviceCredit'))

export const orbitPendingCreditAmountData = (orbitOffer, orbitCredit, translate, lang) => {
  const orbitTextCopy = () => {
    if (isTradeIn(orbitOffer)) {
      const pendingAmountsTradeIn = 'orbit.pendingAmounts.tradeIn'

      if (isOrbitTradeInDeviceCredit(orbitCredit)) {
        return translate(`${pendingAmountsTradeIn}.deviceCredit`)
      }

      if (isOrbitOneTimeCreditType(orbitCredit)) {
        return translate(`${pendingAmountsTradeIn}.oneTimeCredit`)
      }
    }

    if (isOrbitBibDeviceCreditType(orbitCredit)) {
      return translate('orbit.pendingAmounts.bringItBack.bibDeviceCredit')
    }

    return orbitCredit.name
  }

  return {
    id: `orbitPendingAmount_${orbitCredit.name}`,
    price: `-${formatCurrency(orbitCredit.amount, lang)}`,
    name: orbitTextCopy(),
    prefixIcon: 'DEALS'
  }
}

const renameKeys = curry((keysMap, obj) =>
  reduce((acc, key) => assoc(keysMap[key] || key, obj[key], acc), {}, keys(obj))
)

const getOrbitDeviceInformation = pipe(
  path(['productOffering', 'characteristics']),
  map((x) => ({ [x.name]: x.value })),
  mergeAll,
  renameKeys({
    categoryCd: 'category',
    manufacturerCd: 'manufacturer',
    modelCd: 'model',
    storageCd: 'storage'
  })
)

/**
 * Get orbit attribute for basic info item.
 * Use case: to be appended to the attribute array which is mapped in BasicInfo molecule
 * @param {object} orbitOffer - Orbit item
 * @param {function} translate - Translate hook
 * @returns {string[]}
 */
export const getOrbitDeviceAttribute = (orbitOffer, translate) => {
  if (!orbitOffer) return []

  const offerType = isTradeIn(orbitOffer) ? 'tradeIn' : 'bringItBack'
  const deviceInformation = getOrbitDeviceInformation(orbitOffer)
  const offerTypeCopy = translate(`orbit.offer.${offerType}`)

  return [
    `${offerTypeCopy} ${deviceInformation.manufacturer} ${deviceInformation.model} ${deviceInformation.storage}, IMEI : ${deviceInformation.IMEI}`
  ]
}

export const getPendingBalanceFromOffer = (offerItem) => {
  const getOneTimePrices = pipe(
    pathOr([], ['itemPrice', 'oneTime']),
    filter(
      either(
        pipe(prop('name'), includes(__, ['PENALTY_FINANCE', 'PENALTY_HARDWARE'])),
        allPass([
          propEq('name', 'PENALTY_BIB'),
          pipe(propOr([], 'alterations'), none(pipe(prop('name'), includes('_PENDING', __))))
        ])
      )
    )
  )

  return getOneTimePrices(offerItem).reduce((acc, oneTimePrice) => {
    acc.individual.push(oneTimePrice)

    acc.total += oneTimePrice.amount

    if (oneTimePrice.amount > 0) {
      acc.hasPenalty = true
    }

    oneTimePrice.alterations?.forEach(alteration => {
      acc.total -= alteration.amount
    })

    return acc
  }, { individual: [], total: 0, hasPenalty: false })
}


export const getBIBAmount = (device) =>
  pipe(
    pathOr([], ['itemPrice', 'oneTime']),
    find(has('alterations')),
    propOr([], 'alterations'),
    find(pathEq(['type'], 'bringItBackAmount')),
    propOr(0, 'amount')
  )(device)

export const buildOrbitBIBPendingDataList = (offer, lang, translate) => {
  const orbitPenaltyBIBPending = getPenaltyBIBPending(offer)
  return orbitPenaltyBIBPending
    ? orbitPenaltyBIBPending.map((penalty) => ({
        id: penalty.name,
        price: formatCurrency(penalty.amount, lang),
        name: translate('orbit.pendingAmounts.PENALTY_BIB')
      }))
    : []
}

export const buildOrbitRecurringCreditsList = (offer, translate, lang) => {
  const orbitOffer = getOrbitItemFromOffer(offer)
  const orbitItemRecurringCredits = getOrbitItemRecurringCredits(orbitOffer)
  return orbitItemRecurringCredits
    ? orbitItemRecurringCredits.map((orbitCredit) =>
        orbitPendingCreditAmountData(orbitOffer, orbitCredit, translate, lang)
      )
    : []
}

// **** end of ORBIT utils **** //
